import { useEffect, useRef, useCallback } from "react";

// ─── Fallback Meta Embedded Signup Configuration ────────────────────────────
const META_CONFIG_ID = process.env.REACT_APP_META_WHATSAPP_CONFIG_ID || "1229146472634257";  // <-- your Configuration ID
// ─────────────────────────────────────────────────────────────────────────────

const PERMITTED_ORIGINS = [
  "https://www.facebook.com",
  "https://web.facebook.com",
];
const META_SIGNUP_TIMEOUT_MS = Number(process.env.REACT_APP_META_SIGNUP_TIMEOUT_MS || 120000);

// Polls window._fbReady — set to true inside fbAsyncInit in index.html
// only after FB.init() has fully completed.
function waitForFBReady() {
  return new Promise((resolve) => {
    if (window._fbReady) { resolve(); return; }
    const poll = setInterval(() => {
      if (window._fbReady) { clearInterval(poll); resolve(); }
    }, 100);
  });
}

function collectPayloadObjects(raw, seen = new Set()) {
  if (!hasPlainObjectValue(raw) || seen.has(raw)) return [];
  seen.add(raw);

  const nestedKeys = ["data", "payload", "event_data", "eventData", "session_info", "sessionInfo"];
  return [
    raw,
    ...nestedKeys.flatMap((key) => collectPayloadObjects(raw[key], seen)),
  ];
}

function pickPayloadValue(payloads, ...keys) {
  for (const payload of payloads) {
    for (const key of keys) {
      const value = payload?.[key];
      if (value !== undefined && value !== null && value !== "") return value;
    }
  }
  return "";
}

function payloadEventTokens(payloads) {
  return payloads
    .flatMap((payload) => [payload.type, payload.event, payload.status])
    .filter((value) => value !== undefined && value !== null && value !== "")
    .map((value) => String(value).toUpperCase());
}

function isMetaSignupPayload(raw) {
  const payloads = collectPayloadObjects(raw);
  const tokens = payloadEventTokens(payloads);
  return (
    tokens.some((token) => token === "WA_EMBEDDED_SIGNUP") ||
    tokens.some((token) => ["FINISH", "COMPLETE", "COMPLETED", "CANCEL", "CANCELLED", "ERROR", "POPUP_CLOSED"].includes(token)) ||
    !!pickPayloadValue(payloads, "waba_id", "wabaId", "phone_number_id", "phoneNumberId", "business_id", "businessId")
  );
}

function normalise(raw) {
  if (!raw || typeof raw !== "object") return { event: "error", data: {} };

  const payloads = collectPayloadObjects(raw);
  const tokens = payloadEventTokens(payloads);

  if (tokens.some((token) => ["CANCEL", "CANCELLED"].includes(token))) {
    return { event: "cancelled", data: {} };
  }
  if (tokens.some((token) => token === "POPUP_CLOSED")) {
    return { event: "popup_closed", data: {} };
  }
  if (tokens.some((token) => token === "ERROR")) {
    return { event: "error", data: {} };
  }
  if (tokens.some((token) => ["FINISH", "COMPLETE", "COMPLETED"].includes(token))) {
    return {
      event: "completed",
      data: {
        waba_id: pickPayloadValue(payloads, "waba_id", "wabaId"),
        phone_number_id: pickPayloadValue(payloads, "phone_number_id", "phoneNumberId"),
        business_id: pickPayloadValue(payloads, "business_id", "businessId"),
      },
    };
  }
  return { event: "error", data: {} };
}

function summarizeMetaEvent(raw) {
  const normalized = normalise(raw);
  return {
    rawType: raw?.type ?? raw?.event,
    normalizedEvent: normalized.event,
    hasWabaId: !!normalized.data?.waba_id,
    hasPhoneNumberId: !!normalized.data?.phone_number_id,
    hasBusinessId: !!normalized.data?.business_id,
  };
}

function summarizeLoginResponse(response) {
  return {
    status: response?.status,
    hasAuthResponse: !!response?.authResponse,
    hasAuthorizationCode: !!response?.authResponse?.code,
  };
}

function hasPlainObjectValue(value) {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function pickFirst(...values) {
  return values.find((value) => value !== undefined && value !== null && value !== "");
}

function buildLoginOptions(launch = {}) {
  const source = hasPlainObjectValue(launch) ? launch : {};
  const rawLoginOptions = source.login_options || source.loginOptions || source.options;
  const loginOptions = hasPlainObjectValue(rawLoginOptions) ? rawLoginOptions : source;
  const setup = pickFirst(loginOptions.setup, source.setup, {});
  const featureType = pickFirst(loginOptions.feature_type, loginOptions.featureType, source.feature_type, source.featureType);
  const sessionInfoVersion = pickFirst(
    loginOptions.session_info_version,
    loginOptions.sessionInfoVersion,
    source.session_info_version,
    source.sessionInfoVersion
  );
  const extras = pickFirst(loginOptions.extras, source.extras);
  const configId = META_CONFIG_ID;

  return {
    config_id: configId,
    response_type: pickFirst(loginOptions.response_type, loginOptions.responseType, source.response_type, source.responseType, "code"),
    override_default_response_type:
      pickFirst(
        loginOptions.override_default_response_type,
        loginOptions.overrideDefaultResponseType,
        source.override_default_response_type,
        source.overrideDefaultResponseType
      ) ?? true,
    // Merge extras so that featureType + sessionInfoVersion are ALWAYS present
    // in the FB.login() call, even when the backend already provides an extras
    // object (e.g. { setup: {...} }).
    //
    // Priority order (highest → lowest):
    //   1. Fields already inside the backend-provided extras object
    //   2. Top-level featureType / sessionInfoVersion extracted from launch
    //   3. Hardcoded defaults required by Meta's API
    extras: {
      setup,                 // top-level extracted setup (fallback: {})
      ...extras,             // spread backend extras — overrides setup with a richer value if present
      featureType: pickFirst(
        extras?.featureType,
        extras?.feature_type,
        featureType,
        "whatsapp_embedded_signup"
      ),
      sessionInfoVersion: (() => {
        const val = pickFirst(
          extras?.sessionInfoVersion,
          extras?.session_info_version,
          sessionInfoVersion
        );
        return val !== undefined && val !== null && val !== "" ? Number(val) : 3;
      })(),
    },
  };
}

export function useMetaSignup(onResult, onComplete) {
  const timeoutRef = useRef(null);
  const popupCheckRef = useRef(null);
  const popupWindowRef = useRef(null);
  const terminalEventRef = useRef(false);
  const sessionRef = useRef({
    docliftOnboardingSessionId: null,
    docliftSessionToken: null,
    expiresAt: null,
    authCode: null,
    signupData: null,
    submitted: false,
  });

  const cleanup = useCallback(() => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
      // console.log("[MetaSignup] timeout cancelled");
    }
    if (popupCheckRef.current) {
      window.clearInterval(popupCheckRef.current);
      popupCheckRef.current = null;
    }
    popupWindowRef.current = null;
    sessionRef.current = {
      docliftOnboardingSessionId: null,
      docliftSessionToken: null,
      expiresAt: null,
      authCode: null,
      signupData: null,
      submitted: false,
    };
  }, []);

  const emitTerminalResult = useCallback((result) => {
    if (terminalEventRef.current) return;
    terminalEventRef.current = true;
    onResult?.(result);
    cleanup();
  }, [cleanup, onResult]);

  const startTimeout = useCallback(() => {
    if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    timeoutRef.current = window.setTimeout(() => {
      timeoutRef.current = null;
      // console.warn("[MetaSignup] timeout expired", {
      //   onboardingSessionId: sessionRef.current.docliftOnboardingSessionId,
      //   normalizedEvent: "timeout",
      // });
      emitTerminalResult({ event: "timeout", data: {} });
    }, META_SIGNUP_TIMEOUT_MS);

    // console.log("[MetaSignup] timeout started", { timeoutMs: META_SIGNUP_TIMEOUT_MS });
  }, [emitTerminalResult]);

  const startPopupClosedMonitor = useCallback(() => {
    if (!popupWindowRef.current || popupCheckRef.current) return;

    popupCheckRef.current = window.setInterval(() => {
      if (!popupWindowRef.current?.closed) return;

      // Popup is now closed — stop the polling interval immediately.
      window.clearInterval(popupCheckRef.current);
      popupCheckRef.current = null;

      // Give Meta a grace period to fire the WA_EMBEDDED_SIGNUP postMessage
      // and/or for FB.login's auth callback to set authCode / signupData.
      // Both signals are async and can arrive a moment after the popup window
      // closes, so we must not emit popup_closed instantly.
      window.setTimeout(() => {
        // If a terminal event already fired (completed / cancelled / error)
        // or if we already have the signup data or auth code, do nothing.
        if (
          terminalEventRef.current ||
          sessionRef.current.authCode ||
          sessionRef.current.signupData
        ) {
          return;
        }
        // console.warn("[MetaSignup] popup closed without completion", { normalizedEvent: "popup_closed" });
        emitTerminalResult({ event: "popup_closed", data: {} });
      }, 1500);
    }, 500);
  }, [emitTerminalResult]);

  // ─── Emit result once both Meta signals have arrived ──────────────────────
  const trySubmit = useCallback(async () => {
    const s = sessionRef.current;
    if (terminalEventRef.current || !s.authCode || !s.signupData || s.submitted) return;
    s.submitted = true;

    const expiresAtMs = s.expiresAt ? new Date(s.expiresAt).getTime() : null;
    if (expiresAtMs && Number.isFinite(expiresAtMs) && Date.now() >= expiresAtMs) {
      // console.warn("[MetaSignup] completion blocked because onboarding session expired", {
      //   onboardingSessionId: s.docliftOnboardingSessionId,
      //   expiresAt: s.expiresAt,
      // });
      emitTerminalResult({ event: "session_expired", data: {} });
      return;
    }

    const normalizedResult = {
      event: "completed",
      data: {
        ...s.signupData,
      },
    };

    // console.log("[MetaSignup] correlated auth callback and signup event", {
    //   onboardingSessionId: s.docliftOnboardingSessionId,
    //   hasSessionToken: !!s.docliftSessionToken,
    //   hasAuthorizationCode: true,
    //   normalizedEvent: "completed",
    //   hasWabaId: !!s.signupData.waba_id,
    //   hasPhoneNumberId: !!s.signupData.phone_number_id,
    //   hasBusinessId: !!s.signupData.business_id,
    // });

    try {
      if (onComplete) {
        await onComplete({
          onboarding_session_id: s.docliftOnboardingSessionId,
          session_token: s.docliftSessionToken,
          authorization_code: s.authCode,
          session_info: {
            waba_id: normalizedResult.data.waba_id,
            phone_number_id: normalizedResult.data.phone_number_id,
            business_id: normalizedResult.data.business_id,
          },
        });
      }
      terminalEventRef.current = true;
      onResult?.(normalizedResult);
    } catch (error) {
      // console.error("[MetaSignup] complete onboarding submission failed", {
      //   onboardingSessionId: s.docliftOnboardingSessionId,
      //   status: error?.status,
      //   message: error?.message,
      // });
      terminalEventRef.current = true;
      onResult?.({ event: "error", data: {} });
    } finally {
      cleanup();
    }
  }, [cleanup, emitTerminalResult, onComplete, onResult]);

  // ─── Session postMessage listener (WABA ID, Phone ID, Business ID) ────────
  useEffect(() => {
    const handleMessage = (event) => {
      if (!PERMITTED_ORIGINS.includes(event.origin)) {
        // console.warn("[MetaSignup] ignored message from untrusted origin", { origin: event.origin });
        return;
      }

      let data;

      // console.group("===== RAW META MESSAGE =====");
      // console.log("Origin:", event.origin);
      // console.log("Type:", typeof event.data);
      // console.log("Raw Data:", event.data);
      // console.groupEnd();

      if (typeof event.data === "string") {
        try {
          data = JSON.parse(event.data);
        } catch {
          // Keep the raw string
          data = event.data;
        }
      } else {
        data = event.data;
      }

      // console.log("[MetaSignup] message event", {
      //   origin: event.origin,
      //   ...summarizeMetaEvent(data),
      // });

      if (!isMetaSignupPayload(data)) {
        // console.log("[MetaSignup] ignored unrelated Meta message", {
        //   rawType: data?.type ?? data?.event,
        // });
        return;
      }

      const normalized = normalise(data);
      // console.log("[MetaSignup] normalized message event", {
      //   rawType: data?.type ?? data?.event,
      //   normalizedEvent: normalized.event,
      // });

      if (normalized.event === "completed") {
        sessionRef.current.signupData = normalized.data;
        trySubmit();
        return;
      }

      emitTerminalResult(normalized);
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [emitTerminalResult, trySubmit]);

  useEffect(() => cleanup, [cleanup]);

  // ─── Auth code handler ────────────────────────────────────────────────────
  const handleAuthCallback = useCallback((code) => {
    if (terminalEventRef.current || !code) return;
    // console.log("[MetaSignup] authorization callback received", {
    //   onboardingSessionId: sessionRef.current.docliftOnboardingSessionId,
    //   hasAuthorizationCode: true,
    // });
    sessionRef.current.authCode = code;
    trySubmit();
  }, [trySubmit]);

  // ─── Launch Embedded Signup ───────────────────────────────────────────────
  const openSignup = useCallback(async (launch, context = {}) => {
    cleanup();
    terminalEventRef.current = false;
    sessionRef.current.docliftOnboardingSessionId = context.onboardingSessionId || null;
    sessionRef.current.docliftSessionToken = context.sessionToken || null;
    sessionRef.current.expiresAt = context.expiresAt || null;

    // Wait until FB.init() has completed (flag set in index.html fbAsyncInit).
    await waitForFBReady();

    const loginOptions = buildLoginOptions(launch);
    // console.log("[MetaSignup] launch options ready", {
    //   onboardingSessionId: sessionRef.current.docliftOnboardingSessionId,
    //   hasSessionToken: !!sessionRef.current.docliftSessionToken,
    //   expiresAt: sessionRef.current.expiresAt,
    //   configId: loginOptions.config_id,
    //   responseType: loginOptions.response_type,
    //   hasExtras: !!loginOptions.extras,
    //   launchKeys: launch ? Object.keys(launch) : [],
    // });

    if (!loginOptions.config_id) {
      // console.error("[MetaSignup] blocked launch because config_id is missing", {
      //   onboardingSessionId: sessionRef.current.docliftOnboardingSessionId,
      //   normalizedEvent: "error",
      // });
      emitTerminalResult({ event: "error", data: { code: "missing_config_id" } });
      return;
    }

    const originalWindowOpen = window.open;

    window.open = function patchedWindowOpen(...args) {
      const popup = originalWindowOpen.apply(window, args);
      popupWindowRef.current = popup;

      if (!popup) {
        // console.warn("[MetaSignup] popup blocked", { normalizedEvent: "popup_blocked" });
        window.setTimeout(() => emitTerminalResult({ event: "popup_blocked", data: {} }), 0);
        return popup;
      }

      startPopupClosedMonitor();
      return popup;
    };

    try {
      startTimeout();
      // console.log("FINAL LOGIN OPTIONS", JSON.stringify(loginOptions, null, 2));
      window.FB.login(
        (response) => {
          // console.log("[MetaSignup] FB.login response", {
          //   onboardingSessionId: sessionRef.current.docliftOnboardingSessionId,
          //   ...summarizeLoginResponse(response),
          // });
          if (response.authResponse) {
            handleAuthCallback(response.authResponse.code);
          } else {
            // console.log("[MetaSignup] login cancelled or failed", {
            //   onboardingSessionId: sessionRef.current.docliftOnboardingSessionId,
            //   status: response?.status,
            //   normalizedEvent: "cancelled",
            // });
            emitTerminalResult({ event: "cancelled", data: {} });
          }
        },
        loginOptions
      );
    } catch (error) {
      // console.warn("[MetaSignup] FB.login launch failed", {
      //   normalizedEvent: "popup_blocked",
      //   message: error?.message,
      // });
      emitTerminalResult({ event: "popup_blocked", data: {} });
    } finally {
      window.open = originalWindowOpen;
    }
  }, [cleanup, emitTerminalResult, handleAuthCallback, startPopupClosedMonitor, startTimeout]);

  return { openSignup, handleAuthCallback };
}
